import { useState } from "react";
import { useSearchParams } from "react-router-dom";
// @ts-expect-error pure js
import { handleAPIError } from "../utils/index.jsx";
import Button from "../components/UI/Button";
import Card from "../components/UI/Card";
import Callout from "../components/UI/Callout";
// @ts-expect-error pure js
import Spinner from "../components/UI/Spinner";
import Title from "../components/UI/Title";
import Text from "../components/UI/Text";
// @ts-expect-error pure js
import ToggleSwitch from "../components/UI/ToggleSwitch";
import useOAuthAuthorize from "../hooks/use-oauth-authorize";
import styles from "./OAuthAuthorizePage.module.css";

const REQUIRED_PARAMS = [
  "client_id",
  "redirect_uri",
  "response_type",
  "code_challenge",
  "code_challenge_method",
] as const;

function buildErrorRedirect(
  redirectUri: string,
  error: string,
  state: string | null,
): string {
  try {
    const url = new URL(redirectUri);
    url.searchParams.set("error", error);
    if (state) url.searchParams.set("state", state);
    return url.toString();
  } catch {
    const sep = redirectUri.includes("?") ? "&" : "?";
    const stateParam = state ? `&state=${encodeURIComponent(state)}` : "";
    return `${redirectUri}${sep}error=${encodeURIComponent(
      error,
    )}${stateParam}`;
  }
}

export default function OAuthAuthorizePage() {
  const [searchParams] = useSearchParams();
  const params = Object.fromEntries(searchParams.entries());
  const missing = REQUIRED_PARAMS.filter((p) => !params[p]);
  const redirectUri = params.redirect_uri ?? null;
  const state = params.state ?? null;

  const {
    info,
    error: fetchError,
    isLoading,
    grant,
  } = useOAuthAuthorize(params, missing.length === 0);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [writeGranted, setWriteGranted] = useState(true);

  const grantedScopes = (info?.scopes ?? []).filter(
    (scope) => scope !== "write" || writeGranted,
  );

  const missingError =
    missing.length > 0 ? `Paramètres manquants : ${missing.join(", ")}` : "";
  const unknownClient =
    (fetchError as { response?: { status?: number } } | undefined)?.response
      ?.status === 404;
  const error =
    submitError ||
    missingError ||
    (unknownClient
      ? "L'application qui demande l'accès s'est présentée avec un identifiant " +
        "inconnu. Si vous avez rempli les champs « Client ID » ou « Client " +
        "Secret » en configurant le connecteur, supprimez le connecteur puis " +
        "recréez-le en laissant ces champs vides : ils sont remplis " +
        "automatiquement."
      : fetchError
      ? handleAPIError(fetchError)
      : "");

  async function handleAuthorize() {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      window.location.href = await grant(grantedScopes);
    } catch (err) {
      setSubmitError(handleAPIError(err));
      setIsSubmitting(false);
    }
  }

  function handleDeny() {
    if (!redirectUri) return;
    window.location.href = buildErrorRedirect(
      redirectUri,
      "access_denied",
      state,
    );
  }

  return (
    <div className={styles.page}>
      <Card>
        <Title variant="hecto">Autoriser l'accès à votre compte Qalimo</Title>

        <Callout variant="danger" className={styles.errorCard}>
          {error}
        </Callout>

        {isLoading && !error && (
          <div className={styles.loading}>
            <Spinner />
          </div>
        )}

        {info && (
          <>
            <Text className={styles.section}>
              <Text as="strong" weight="bold">
                {info.client_name}
              </Text>{" "}
              souhaite accéder à votre compte Qalimo&nbsp;
              <Text as="strong" weight="bold">
                {info.compte_mail}
              </Text>
              . Après autorisation, vous serez redirigé vers{" "}
              <Text as="strong" weight="bold">
                {info.redirect_target}
              </Text>
              .
            </Text>

            {!info.client_verified && (
              <Callout
                variant="warning"
                title="Application non vérifiée"
                className={styles.section}
              >
                Qalimo ne connaît pas cette application. Son nom est déclaré par
                elle-même et ne garantit rien. N'autorisez l'accès que si vous
                êtes à l'origine de cette demande et que la destination
                ci-dessus vous est familière.
              </Callout>
            )}

            <div className={styles.section}>
              <Text weight="medium" className={styles.fieldLabel}>
                Permissions demandées
              </Text>
              <div className={styles.scopeList}>
                {info.scopes.map((scope) => (
                  <div key={scope} className={styles.scopeRow}>
                    <ToggleSwitch
                      status={scope !== "write" || writeGranted}
                      onToggle={() =>
                        scope === "write" && setWriteGranted((v) => !v)
                      }
                      hasApiCall={false}
                      disabled={scope !== "write"}
                    />
                    <Text variant="deci">
                      {scope === "write"
                        ? "Modifier vos données (créer des quittances, écritures, etc.)"
                        : "Lire vos données (biens, locataires, soldes)"}
                    </Text>
                  </div>
                ))}
              </div>
            </div>

            <Text color="muted" variant="deci" className={styles.section}>
              Vous pourrez révoquer cet accès à tout moment depuis votre compte.
            </Text>

            <div className={styles.actions}>
              <Button
                variant="secondary"
                onClick={handleDeny}
                disabled={isSubmitting}
              >
                Refuser
              </Button>
              <Button
                onClick={handleAuthorize}
                disabled={isSubmitting || grantedScopes.length === 0}
              >
                {isSubmitting ? <Spinner /> : "Autoriser"}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
